<template>
  <div>
    <v-row>
      <v-col>
        <vs-heading type="overline">Job Signature</vs-heading>
      </v-col>
      <v-col class="shrink">
        <DisabledActionTooltip :permission="signaturePermission" :label="buttonLabel">
          <template #default="{ disabled }">
            <vs-button
              data-test="edit-signature-button"
              type="tertiary"
              small
              icon="mdi-draw-pen"
              :label="buttonLabel"
              :disabled="disabled"
              @click="openSignatureBottomSheet"
            />
          </template>
        </DisabledActionTooltip>
      </v-col>
    </v-row>
    <vs-wrapper v-if="isError" space="base">
      <div />
      <template v-if="signatureError.status == 403">
        <vs-text data-test="no-permission-caption" type="caption"
          >You don't have permission to view this section</vs-text
        >
      </template>
      <v-alert v-else dense outlined shaped type="error"> Unable to load signature. </v-alert>
    </vs-wrapper>
    <vs-wrapper v-else space="small">
      <v-skeleton-loader v-if="isInitialLoading" type="image" rounded />
      <div v-if="!isLoading && signature">
        <remove-signature-confirmation-dialog
          v-if="showConfirmationDialog"
          @close="showConfirmationDialog = false"
          @confirm="clearSignature"
        />
        <div v-if="signatureURL" class="container">
          <div class="btn">
            <DisabledActionTooltip :permission="signaturePermission" label="Remove signature">
              <template #default="{ disabled }">
                <vs-button
                  data-test="remove-signature-button"
                  aria-label="Remove signature"
                  type="tertiary"
                  destructive
                  small
                  icon="clear"
                  :disabled="disabled"
                  @click="openRemoveSignatureDialog"
                />
              </template>
            </DisabledActionTooltip>
          </div>
          <img :src="signatureURL" />
        </div>
        <v-row>
          <v-col>
            <vs-text type="caption">{{ signature.name }}</vs-text>
          </v-col>
          <v-col class="shrink">
            <vs-text type="caption">{{ formatDateTimeWithYear(signature.signedAt) }}</vs-text>
          </v-col>
        </v-row>
      </div>
      <div v-else style="padding-top: 22px">
        <vs-text type="caption" data-test="no-signature-caption">Not signed yet</vs-text>
      </div>
    </vs-wrapper>
  </div>
</template>

<script setup lang="ts">
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { allOf, check, usePermission } from '@/components/common/permission'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsText from '@/components/vision/VsText.vue'
import RemoveSignatureConfirmationDialog from '@/components/jobs/sections/RemoveSignatureConfirmationDialog.vue'
import JobSignature from '@/models/job-signature'
import { useJobSignatureQuery, useDeleteSignatureMutation } from '@/api/signature'
import { computed, ref } from 'vue'
import { formatDateTimeWithYear } from '@/utils/date-formatting'
import { ApprovalStatus, JobStatus } from '@/models/job'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'

const props = defineProps<{
  jobId: string
  jobStatus: string
  jobApprovalStatus: string
  signature: JobSignature | null
}>()

const signatureIdRef = computed(() => props.signature?.id)

const {
  data: signatureBlob,
  isInitialLoading,
  isLoading,
  isError,
  error: signatureError,
} = useJobSignatureQuery(props.jobId, signatureIdRef)

const { mutateAsync: deleteSignature } = useDeleteSignatureMutation()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const emit = defineEmits<{
  (e: 'clear-signature')
}>()

const showConfirmationDialog = ref<boolean>()

const editableStatuses: string[] = [JobStatus.ACTIVE, JobStatus.PAUSED, JobStatus.COMPLETED]

const { permission: signaturePermission, allowed: canEditSignature } = usePermission(() =>
  allOf(
    check(
      signatureError.value?.status !== 403,
      'You do not have permission to edit this signature.',
    ),
    check(
      editableStatuses.includes(props.jobStatus),
      'The job must be active, paused, or completed.',
    ),
    check(
      props.jobStatus !== JobStatus.COMPLETED ||
        props.jobApprovalStatus !== ApprovalStatus.APPROVED,
      'This completed job has been approved.',
    ),
  ),
)

const signatureURL = computed(() => {
  if (!signatureBlob.value) return null
  return URL.createObjectURL(signatureBlob.value)
})

const buttonLabel = computed(() => {
  if (props.signature == null) {
    return 'Add signature'
  } else {
    return 'Re-sign'
  }
})

const clearSignature = async () => {
  if (!canEditSignature.value || !props.signature) return

  await deleteSignature({
    signatureId: props.signature.id,
    jobId: props.jobId,
  })

  showConfirmationDialog.value = false
  emit('clear-signature')
}

const openRemoveSignatureDialog = () => {
  if (!canEditSignature.value || !signatureURL.value) return
  showConfirmationDialog.value = true
}

const openSignatureBottomSheet = () => {
  if (!canEditSignature.value) return
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isSignatureBottomSheetVisible: true,
  })
}
</script>
<style scoped>
.container {
  position: relative;
}

.btn {
  position: absolute;
  top: 10%;
  right: 0%;
}
</style>
